import { HttpClient } from '@/shared';
import {
  BulkAssignInput,
  IRemediationRepository,
  RemediationQueueParams,
  RemediationQueueResult,
  SaveCorrectionInput,
  SaveCorrectionsBatchInput,
  Section2DocumentInput,
} from '../../application/ports/outbound/IRemediationRepository';
import {
  EmployeeIdentityRequirements,
  EmployeeSubmitInput,
  EmployeeSubmitResult,
  EmployeeVerifyInput,
  EmployeeVerifyResult,
  RemediationActor,
  RemediationAuditEvent,
  RemediationRecordDetail,
  RemediationVerification,
} from '../../application/domain/entities/Remediation';

/** Subset of the /app-users response used to build the assignee picker. */
interface AppUserDto {
  appUserId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Justifications are free text, so a "reason" of `'   '` is indistinguishable from none — but only
 * once it is trimmed. Normalising at this boundary (rather than per card) means every caller, current
 * and future, sends the same thing: a real reason, or nothing at all. The backend trims and re-checks
 * on its side too; this keeps the client from ever presenting whitespace as an audit record.
 */
function trimJustification(justification?: string | null): string | undefined {
  const trimmed = justification?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * REST adapter for the client-remediation backend (`/remediation/*`). Uses the shared axios
 * HttpClient (returns the ApiResponse envelope → unwrap `res.data`). Mirrors ApiBatchProcessingRepository.
 */
export class ApiRemediationRepository implements IRemediationRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getQueue(params: RemediationQueueParams): Promise<RemediationQueueResult> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.locationId) qs.set('locationId', params.locationId);
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.sort) qs.set('sort', params.sort);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await this.httpClient.get<RemediationQueueResult>(`/remediation/queue${suffix}`);
    return res.data ?? { items: [], total: 0 };
  }

  async getRecord(id: string): Promise<RemediationRecordDetail> {
    const res = await this.httpClient.get<RemediationRecordDetail>(`/remediation/records/${id}`);
    return res.data;
  }

  async saveCorrection(id: string, input: SaveCorrectionInput): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/corrections`, {
      ...input,
      justification: trimJustification(input.justification),
    });
  }

  async saveCorrectionsBatch(id: string, input: SaveCorrectionsBatchInput): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/corrections-batch`, {
      ...input,
      justification: trimJustification(input.justification),
    });
  }

  async markSection2Signed(id: string, justification: string): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/section2-signature`, {
      justification: trimJustification(justification),
    });
  }

  async saveSection2Document(id: string, input: Section2DocumentInput): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/section2-document`, {
      ...input,
      justification: trimJustification(input.justification),
    });
  }

  async uploadDocument(id: string, file: string, fileName: string): Promise<string> {
    const res = await this.httpClient.post<{ fileUrl: string }>(
      `/remediation/records/${id}/document-upload`,
      { file, fileName }
    );
    return res.data.fileUrl;
  }

  async dismissFinding(id: string, findingId: string, justification: string): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/dismiss-finding`, {
      findingId,
      justification: trimJustification(justification),
    });
  }

  async assign(id: string, assigneeUserId?: string): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/assign`, { assigneeUserId });
  }

  async bulkAssign(input: BulkAssignInput): Promise<void> {
    await this.httpClient.post('/remediation/assign', input);
  }

  async sendToEmployee(id: string, employeeEmail: string, employeePhone?: string): Promise<void> {
    const phone = employeePhone?.trim();
    await this.httpClient.post(`/remediation/records/${id}/send-to-employee`, {
      employeeEmail,
      ...(phone ? { employeePhone: phone } : {}),
    });
  }

  async skipEmployee(id: string, reason: string): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/skip-employee`, { reason });
  }

  async addSection1Item(id: string, fieldPath: string, note?: string): Promise<void> {
    const trimmed = note?.trim();
    await this.httpClient.post(`/remediation/records/${id}/section1-items`, {
      fieldPath,
      ...(trimmed ? { note: trimmed } : {}),
    });
  }

  async removeSection1Item(id: string, findingId: string): Promise<void> {
    await this.httpClient.delete(
      `/remediation/records/${id}/section1-items/${encodeURIComponent(findingId)}`
    );
  }

  async assignLocation(id: string, locationId: string): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/assign-location`, { locationId });
  }

  async complete(id: string): Promise<void> {
    await this.httpClient.post(`/remediation/records/${id}/complete`, {});
  }

  async getVerification(id: string): Promise<RemediationVerification> {
    const res = await this.httpClient.get<RemediationVerification>(
      `/remediation/records/${id}/verification`
    );
    return res.data;
  }

  async getAudit(id: string): Promise<RemediationAuditEvent[]> {
    const res = await this.httpClient.get<RemediationAuditEvent[]>(
      `/remediation/records/${id}/audit`
    );
    return res.data ?? [];
  }

  async getAssignableUsers(accountId: string): Promise<RemediationActor[]> {
    // Client-company users for the assignee picker. Account-scoped, so internal VerifyToday staff
    // (who live under a separate internal account) are excluded.
    const res = await this.httpClient.get<AppUserDto[]>(
      `/app-users?accountId=${encodeURIComponent(accountId)}`
    );
    return (res.data ?? []).map((u) => ({
      id: u.appUserId,
      name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.appUserId,
    }));
  }

  // ---- Employee (public, capability-token) ----
  async employeeIdentityRequirements(token: string): Promise<EmployeeIdentityRequirements> {
    // POST, not GET: the token is a capability credential, and a query string would land in access
    // logs and browser history.
    const res = await this.httpClient.post<EmployeeIdentityRequirements>(
      '/remediation/employee/identity-requirements',
      { token }
    );
    return res.data;
  }

  async employeeVerify(input: EmployeeVerifyInput): Promise<EmployeeVerifyResult> {
    const res = await this.httpClient.post<EmployeeVerifyResult>(
      '/remediation/employee/verify',
      input
    );
    return res.data;
  }

  async employeeSubmit(input: EmployeeSubmitInput): Promise<EmployeeSubmitResult> {
    const res = await this.httpClient.post<EmployeeSubmitResult>(
      '/remediation/employee/submit',
      input
    );
    return res.data;
  }
}
